import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ShieldCheck, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { universities as fallbackUnis } from '../data/mock';
import { useUniversities } from '../hooks/useUniversities';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => {
  const src = u.building;
  if (!src) return '';
  return src.startsWith('/') ? `${BASE}${src}` : src;
};

export default function TopUniversities() {
  const list = useUniversities(fallbackUnis);
  const universities = list.length ? list : fallbackUnis;
  const ref = React.useRef(null);
  const scroll = (dir) => { if (ref.current) ref.current.scrollBy({ left: dir === 'left' ? -288 : 288, behavior: 'smooth' }); };

  return (
    <section id="universities" className="py-24 bg-muted/50">
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-sm font-bold text-secondary uppercase tracking-widest mb-2">Premium Institutions</h2>
            <h3 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">Top Medical Universities in Russia</h3>
            <p className="text-lg text-muted-foreground">We partner only with the most prestigious government medical universities with a proven track record of Indian student success.</p>
          </div>
          <Link to="/universities">
            <Button variant="outline" className="shrink-0 border-primary/20 text-primary hover:bg-primary hover:text-white rounded-full font-semibold">
              View All Universities <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="relative">
          <button onClick={() => scroll('left')} aria-label="Scroll left"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll('right')} aria-label="Scroll right"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronRight className="h-4 w-4" />
          </button>

          <div ref={ref} className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 md:-mx-8 md:px-8 snap-x snap-mandatory scrollbar-hide">
            {universities.map((u) => (
              <Link to={`/universities/${u.slug}`} key={u.slug} className="shrink-0 w-64 snap-start group rounded-2xl overflow-hidden bg-white shadow-md border border-border hover:shadow-xl transition-all duration-300 block card-lift">
                <div className="relative h-40 overflow-hidden">
                  <img src={resolveImg(u)} alt={u.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute bottom-2 left-2 bg-white rounded-lg shadow p-1 h-9 w-9 flex items-center justify-center font-serif font-bold text-primary text-sm">
                    {u.logo}
                  </div>
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2.5 py-0.5 rounded-full text-[10px] font-bold text-primary shadow-sm flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-brand" /> WHO Approved
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-2">
                    <MapPin className="h-3 w-3 text-brand shrink-0" /> {u.city}, Russia
                  </div>
                  <h4 className="font-serif text-sm font-bold text-foreground mb-3 line-clamp-2 leading-snug">{u.name}</h4>
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase font-semibold">Tuition / yr</div>
                      <div className="font-serif text-base font-bold text-primary">{u.fee}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-brand group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
